import {
  ChevronDown,
  ChevronUp,
  FileImage,
  Plus,
  Trash2,
} from "lucide-react";

import type { ManuscriptFigure } from "../domain/manuscript";

type FiguresEditorProps = {
  figures: ManuscriptFigure[];
  onAdd: () => void;
  onChange: (
    id: string,
    patch: Partial<Omit<ManuscriptFigure, "id">>,
  ) => void;
  onDelete: (id: string) => void;
  onMove: (id: string, direction: -1 | 1) => void;
};

export function FiguresEditor({
  figures,
  onAdd,
  onChange,
  onDelete,
  onMove,
}: FiguresEditorProps) {
  return (
    <main className="editor-panel">
      <div className="editor-hero">
        <div className="hero-icon">
          <FileImage size={22} />
        </div>
        <div>
          <span className="eyebrow">FIGURE LEGENDS</span>
          <h1>独立管理图注</h1>
          <p>
            每张图单独填写图题和图注说明；本模块先管理文字，不上传图片，导出后可直接作为 Word 图注清单。
          </p>
        </div>
      </div>

      <section className="editor-card figure-intro-card">
        <div className="figure-intro-copy">
          <strong>图注与表格一样独立</strong>
          <span>
            图号按当前顺序自动生成，正文请用 Figure 1 或 Fig. 1 引用；拖动上移/下移即可调整编号。
          </span>
        </div>
        <button className="figure-add-button" onClick={onAdd} type="button">
          <Plus size={15} /> 添加图注
        </button>
      </section>

      {figures.length ? (
        figures.map((figure, figureIndex) => (
          <section className="editor-card figure-legend-card" key={figure.id}>
            <header className="figure-card-header">
              <div>
                <span className="figure-number">Figure {figureIndex + 1}</span>
                <strong>图注条目</strong>
              </div>
              <div className="figure-card-actions">
                <button
                  aria-label={`上移 Figure ${figureIndex + 1}`}
                  disabled={figureIndex === 0}
                  onClick={() => onMove(figure.id, -1)}
                  type="button"
                >
                  <ChevronUp size={14} />
                </button>
                <button
                  aria-label={`下移 Figure ${figureIndex + 1}`}
                  disabled={figureIndex === figures.length - 1}
                  onClick={() => onMove(figure.id, 1)}
                  type="button"
                >
                  <ChevronDown size={14} />
                </button>
                <button
                  aria-label={`删除 Figure ${figureIndex + 1}`}
                  className="danger-icon"
                  onClick={() => onDelete(figure.id)}
                  type="button"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </header>

            <label>
              <span className="field-label">图题（标题句）</span>
              <input
                aria-label={`Figure ${figureIndex + 1} 图题`}
                onChange={(event) =>
                  onChange(figure.id, { title: event.target.value })
                }
                placeholder="例如：实验流程与分组设计"
                value={figure.title}
              />
            </label>
            <label>
              <span className="field-label">图注说明</span>
              <textarea
                aria-label={`Figure ${figureIndex + 1} 图注说明`}
                onChange={(event) =>
                  onChange(figure.id, { legend: event.target.value })
                }
                placeholder="填写面板说明、缩写、统计方法和其他图注文字；不需要重复输入 Figure 1。"
                rows={5}
                value={figure.legend}
              />
            </label>
          </section>
        ))
      ) : (
        <section className="editor-card figure-empty-card">
          <FileImage size={26} />
          <strong>还没有图注</strong>
          <span>点击上方“添加图注”，先建立独立的 Figure legend。</span>
        </section>
      )}
    </main>
  );
}
